import { Injectable } from "@angular/core";
import { databases, ID, storage } from "../config/appwrite";
import { environment } from "../../../environments/environment";
import { Servicio } from "../models";
import { Query } from "appwrite";

@Injectable({
  providedIn: "root",
})
export class PaquetesAdquiridosService {
  private collectionId = "paquetes_adquiridos";
  private DATABASE_NAME = environment.databaseName;

  constructor() {}

  /**
   * @getData()
   * @returns
   */
  getData(): Promise<Servicio[]> {
    return databases
      .listDocuments<Servicio>(this.DATABASE_NAME, this.collectionId)
      .then((response) => response.documents);
  }

  /**
   * @createDocument()
   * @param data
   * @returns
   */
  createDocument(data: any) {
    return databases.createDocument(
      this.DATABASE_NAME,
      this.collectionId,
      ID.unique(),
      data
    );
  }

  /**
   * getPaquetes();
   * @returns
   */
  getDataByPaciente(idPaciente: string): Promise<Servicio[]> {
    return databases
      .listDocuments<Servicio>(
        this.DATABASE_NAME,
        this.collectionId,
        // [Query.equal('idPaciente', idPaciente), Query.equal('activo', true), Query.greaterThan('citasRestantes', 0)]
        [
          Query.equal("idPaciente", idPaciente),
          Query.greaterThan("citasRestantes", 0),
        ]
      )
      .then((response) => response.documents);
  }

  /**
   *
   * @param id
   * @param data
   * @returns
   */
  updateDocument(id: string, data: any) {
    return databases.updateDocument(
      this.DATABASE_NAME,
      this.collectionId,
      id,
      data
    );
  }

  /**
   * Devuelve la sesión al paquete cuando se cancela o reprograma una cita
   * que se había descontado de un paquete.
   *
   * Las citas guardan en idPaqueteAdquirido de qué paquete se descontaron y se
   * usa ese dato. Las citas anteriores a ese campo no lo tienen: para ellas se
   * busca el paquete cuyo servicio corresponde al de la cita (misma modalidad,
   * duración, tipo de consulta, tipo de terapia y público).
   *
   * @param idPaciente
   * @param cita cita cancelada, con su servicio expandido
   * @returns el paquete actualizado, o null si no se encontró a cuál devolver
   */
  async devolverPaquete(idPaciente: string, cita?: any) {
    // Caso exacto: la cita sabe de qué paquete salió
    const idPaqueteUsado = cita?.idPaqueteAdquirido;
    if (idPaqueteUsado) {
      try {
        const paqueteUsado = await databases.getDocument(
          this.DATABASE_NAME,
          this.collectionId,
          idPaqueteUsado
        );

        return databases.updateDocument(
          this.DATABASE_NAME,
          this.collectionId,
          idPaqueteUsado,
          { citasRestantes: paqueteUsado["citasRestantes"] + 1 }
        );
      } catch (error) {
        // El paquete pudo haber sido eliminado: se continúa por equivalencia
        console.warn(
          "No se pudo devolver al paquete registrado en la cita:",
          idPaqueteUsado,
          error
        );
      }
    }

    const paquetes = await databases
      .listDocuments(this.DATABASE_NAME, this.collectionId, [
        Query.equal("idPaciente", idPaciente),
        Query.orderDesc("fCompra"),
        Query.limit(100),
      ])
      .then((response) => response.documents);

    if (paquetes.length === 0) {
      console.warn("No hay paquetes a los cuales devolver la sesión");
      return null;
    }

    const idRelacion = (valor: any) => (valor?.$id ?? valor ?? "").toString();
    const servicioCita = cita?.servicioId;

    // Paquetes cuyo servicio corresponde al de la cita cancelada
    const compatibles = servicioCita
      ? paquetes.filter((paquete: any) => {
          const servicioPaquete = paquete?.idServicio;
          return (
            (servicioPaquete?.modalidad || "").toString().trim().toLowerCase() ===
              (servicioCita?.modalidad || "").toString().trim().toLowerCase() &&
            Number(servicioPaquete?.duracion) ===
              Number(servicioCita?.duracion) &&
            idRelacion(servicioPaquete?.tConsulta) ===
              idRelacion(servicioCita?.tConsulta) &&
            idRelacion(servicioPaquete?.tTerapia) ===
              idRelacion(servicioCita?.tTerapia) &&
            idRelacion(servicioPaquete?.tDirigido) ===
              idRelacion(servicioCita?.tDirigido)
          );
        })
      : [];

    // Si no se puede determinar el servicio de la cita, se conserva el
    // comportamiento anterior: el paquete comprado más recientemente.
    const paquete = compatibles.length > 0 ? compatibles[0] : paquetes[0];

    if (compatibles.length === 0) {
      console.warn(
        "No se encontró un paquete equivalente al servicio de la cita; se devuelve al más reciente",
        { idCita: cita?.$id }
      );
    }

    return databases.updateDocument(
      this.DATABASE_NAME,
      this.collectionId,
      paquete["$id"],
      { citasRestantes: paquete["citasRestantes"] + 1 }
    );
  }
}
